import { useCallback, useContext, useEffect, useRef } from 'react'

import { EDITOR_TOOLS } from '@editor/config/constants'
import { INITIAL_SHAPE_STATE } from '@editor/contexts/Editor'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import useDetectShapes from '@editor/hooks/useDetectShapes'
import { Editor } from '@editor/interfaces/canvas'
import { EditorConfig } from '@editor/interfaces/editor'
import { FileProcessingStatus } from '@editor/interfaces/file'
import { PlaneSide, ShapeKeyType } from '@editor/interfaces/shape'
import { setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import { COMMON_MESSAGE, ERROR_PROCESS, getErrorText } from '@editor/services/ErrorHandler'
import { detectShapes } from '@editor/services/InspectionArea'
import { withPromiseContained } from '@editor/services/Util'
import ShapeDetectionError from '@editor/services/errors/ShapeDetectionError'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { addWorkingTool, removeWorkingTool } from '@editor/store/tool'
import { toaster } from '@editor/system/toaster'
import { cloneDeep } from 'lodash'

import { clearSavedPlanes, copyErrorPlanes, copySavingPlanes, reset, resetWorking, resetWorkingPoints } from '../store'

export default function useEditor(props: Editor): EditorConfig {
  const { selectedTool, isPreviousTool, changeTool } = props

  // Toast
  const toastIdRef = useRef<string>(undefined)

  // Store
  const dispatch = useAppDispatch()
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)
  const inspectionSheet = useAppSelector((state) => state.inspectionItems.inspectionSheet)
  const maskRegions = useAppSelector((state) => state.maskPCD.regions)
  const planes = useAppSelector((state) => state.toolPlaneDetection.planes)
  const isDirty = useAppSelector((state) => state.toolPlaneDetection.isDirty)
  const workingPoints = useAppSelector((state) => state.toolPlaneDetection.workingPoints)

  // Context
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useContext(GlobalModalContext)

  // Vars
  const isToolSelected = selectedTool === EDITOR_TOOLS.PLANE
  const hasWorkingPlane = workingPoints.length
  const isSaveable = isDirty && planes.length > 0 && !hasWorkingPlane

  // Hooks
  const { poll } = useDetectShapes(ShapeKeyType.POLYGON, true)

  /**
   * Toggle info panels when tool is selected
   */
  useEffect(() => {
    if (!isToolSelected) {
      if (isPreviousTool(EDITOR_TOOLS.PLANE) && selectedTool !== EDITOR_TOOLS.FOCUS) {
        dispatch(resetWorkingPoints())
      }
    }
  }, [isToolSelected, selectedTool, isPreviousTool, dispatch])

  /**
   * Start rebar detection
   */
  const handleSave = useCallback(async () => {
    if (inspectionArea?.downsample_status?.status !== FileProcessingStatus.SUCCEEDED || !project || !inspectionSheet) {
      return false
    }

    const token = await getAccessToken()
    if (!token) {
      return false
    }

    // copy completed planes over to saving planes so user can create more planes while saving
    dispatch(copySavingPlanes())
    dispatch(addWorkingTool(EDITOR_TOOLS.PLANE))

    const tempPlanes = cloneDeep(planes) // need to use later
    const savingIds = planes.map(({ id }) => id)
    return withPromiseContained(async () => {
      const result = await detectShapes(
        token,
        project.project_id,
        inspectionArea.inspection_area_id,
        inspectionSheet.inspection_sheet_id,
        ShapeKeyType.POLYGON,
        {
          ...INITIAL_SHAPE_STATE(),
          polygons: planes.map((label) => ({
            points: label.points,
            plane_side: PlaneSide.LOWER,
            is_virtual: true, // Standalone planes are always virtual to enhance speed and keep dimensions
          })),
        },
        maskRegions.filter((region) => !region.invisible),
        showErrorModal,
      )
      if (!result) {
        throw new Error('Plane detection failed')
      }

      const pollResult = await poll(result, 2500)
      if (!pollResult) {
        throw new Error('Plane detection failed')
      }

      if (toastIdRef.current) toaster.dismiss(toastIdRef.current)
      toastIdRef.current = toaster.create({
        title: '面積の保存が完了しました',
        type: 'success',
        duration: 5000,
        closable: true,
      })

      dispatch(clearSavedPlanes(savingIds))
    })
      .catch((err) => {
        if (err instanceof ShapeDetectionError) {
          // for each of the result, find the planes that errored and link it back to `planes` through coordinates matching.
          const errorPlanes = tempPlanes
            .map<{ id: string; error: string } | null>((plane) => {
              const error = err.result.find((result) =>
                result.positions?.every((coord) =>
                  plane.points.some((point) => point[0] === coord.x && point[1] === coord.y && point[2] === coord.z),
                ),
              )

              if (!error?.error_id) return null

              return { id: plane.id, error: getErrorText(ERROR_PROCESS.DETECT_SHAPES, error.error_id) }
            })
            .filter(Boolean) as { id: string; error: string }[]

          if (errorPlanes.length) {
            dispatch(copyErrorPlanes(errorPlanes))
            dispatch(clearSavedPlanes(savingIds.filter((id) => !errorPlanes.some((plane) => plane.id === id))))
            return
          }
        } else if (err instanceof Error) {
          dispatch(copyErrorPlanes(savingIds.map((id) => ({ id, error: err.message }))))
          return
        }

        dispatch(copyErrorPlanes(savingIds.map((id) => ({ id, error: `不明な${COMMON_MESSAGE}` }))))
      })
      .finally(() => {
        dispatch(removeWorkingTool(EDITOR_TOOLS.PLANE))
      })
  }, [inspectionArea, inspectionSheet, project, planes, maskRegions, getAccessToken, showErrorModal, poll, dispatch])

  /**
   * Handle apply operation.
   */
  const handleApply = useCallback(async () => {
    dispatch(setAttentionText({ message: '面積を計算中...' }))
    await handleSave()
    dispatch(setAttentionText({ message: '' }))
  }, [dispatch, handleSave])

  /**
   * Handle submit operation.
   * It's basically save + switch to MOVE/SELECT tool.
   */
  const handleSubmit = useCallback(() => {
    // Switch to MOVE tool
    changeTool(EDITOR_TOOLS.MOVE, true)

    if (isSaveable) {
      if (toastIdRef.current) toaster.dismiss(toastIdRef.current)
      toastIdRef.current = toaster.create({
        title: '面積計算を開始しました',
        description:
          '面積計算が進行中です。完了をお待ちの間、他のツールを引き続きご利用いただけます。完了次第、通知いたします。',
        type: 'info',
        duration: 5000,
        closable: true,
      })

      void handleSave()
    }
  }, [changeTool, handleSave, isSaveable])

  /**
   * Reset on unmount
   */
  useEffect(
    () => () => {
      dispatch(reset())
    },
    [dispatch],
  )

  return {
    buttons: {
      apply: {
        key: 'plane-detection-apply',
        label: `計算`,
        loadingLabel: '計算中',
        onClick: handleApply,
        isShown: () => isToolSelected,
        isLoading: () => false,
        isDisabled: () => !planes.length,
      },
      submit: {
        key: 'plane-detection-submit',
        label: isDirty ? '計算して終了' : undefined,
        loadingLabel: '計算中',
        isLoading: () => false,
        isShown: () => isToolSelected,
        isDisabled: () => isDirty && !isSaveable,
        onClick: handleSubmit,
      },
      complete: {
        tooltip:
          hasWorkingPlane && workingPoints.length < 3
            ? `この平面の作成を完了するには、あと${3 - workingPoints.length}点以上が必要です`
            : undefined,
        isShown: () => isToolSelected, // && currentVertexSubtool === SubtoolVertex.AddVertex,
        isDisabled: () => workingPoints.length < 3,
      },
      undo: {
        isShown: () => isToolSelected,
        isDisabled: () => !isDirty,
      },
      cancel: {
        isShown: () => isToolSelected, //  && currentVertexSubtool === SubtoolVertex.AddVertex,
        isDisabled: () => !hasWorkingPlane,
      },
      reset: {
        onClick: () => dispatch(resetWorking()),
        isShown: () => isToolSelected,
        isDisabled: () => !isDirty,
      },
    },
  }
}
